<template>
    <Dialog :visible="showModal" modal @update:visible="v => emit('update:showModal', v)"
        class="w-full md:w-3/4 2xl:w-2/3">
        <template #header>
            <div class="inline-flex items-center justify-center gap-2">
                <h3 class="text-lg font-medium">
                    Neue anstehende Aufgabe hinzufügen
                </h3>
            </div>
        </template>
        <div>
            <Form v-slot="$form" class="gap-4 w-full" :initialValues :resolver @submit="handleSubmit" ref="formRef">
                <Tabs v-model:value="tabValue">
                    <TabList>
                        <Tab value="0">{{ $t("chooseTask") }}</Tab>
                        <Tab value="1">{{ $t("createOneTimeTask") }}</Tab>
                    </TabList>
                    <TabPanels class="mx-0 mt-4 px-0!">
                        <TabPanel value="0">
                            <FloatLabel variant="in" fluid>
                                <Select id="existingChore" name="selectedChoreId" :options="selectableChores" filter
                                    option-value="id" option-disabled="disabled" optionLabel="name" class="w-full"
                                    showClear>
                                    <template #option="slotProps">
                                        <div class="flex items-center">
                                            {{ slotProps.option.name }}
                                            <template v-if="slotProps.option.disabled">
                                                <span>(Bereits
                                                    anstehend)</span>
                                            </template>
                                        </div>
                                    </template>
                                </Select>
                                <label for="existingChore">{{ $t("task") }}*</label>
                            </FloatLabel>
                            <Message v-if="$form.selectedChoreId?.invalid" severity="error" size="small"
                                variant="simple">{{
                                    $form.selectedChoreId.error?.message }}</Message>
                        </TabPanel>
                        <TabPanel value="1" class="space-y-4">
                            <div>
                                <FloatLabel variant="in">
                                    <InputText id="newChoreName" name="newChoreFormName" fluid type="text" />
                                    <label for="newChoreName">{{ $t("name") }}*</label>
                                </FloatLabel>
                                <Message v-if="$form.newChoreFormName?.invalid" severity="error" size="small"
                                    variant="simple">
                                    <ul class="my-0 px-4 flex flex-col gap-1">
                                        <li v-for="(error, index) of $form.newChoreFormName.errors" :key="index">{{
                                            error.message }}</li>
                                    </ul>
                                </Message>
                            </div>
                            <div>
                                <FloatLabel variant="in">
                                    <Textarea id="newChoreDescription" name="newChoreFormDescription" rows="5" cols="30"
                                        style="resize: none" fluid />
                                    <label for="newChoreDescription">{{ $t("description") }}</label>
                                </FloatLabel>
                                <Message v-if="$form.newChoreFormDescription?.invalid" severity="error" size="small"
                                    variant="simple">{{
                                        $form.newChoreFormDescription.error?.message }}</Message>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <FloatLabel variant="in" fluid>
                                        <Select id="newChoreFormDifficultyLevel" name="newChoreFormDifficultyLevel"
                                            :options="sortedDifficulties" filter class="w-full" showClear
                                            option-label="label" option-value="value" />
                                        <label for="newChoreFormDifficultyLevel">{{ $t("difficulty") }}*</label>
                                    </FloatLabel>
                                    <Message v-if="$form.newChoreFormDifficultyLevel?.invalid" severity="error"
                                        size="small" variant="simple">{{
                                            $form.newChoreFormDifficultyLevel.error?.message }}</Message>
                                </div>
                                <div>
                                    <FloatLabel variant="in">
                                        <InputNumber inputId="newChoreFormEstimatedMinutes" :allow-empty="false"
                                            name="newChoreFormEstimatedMinutes" fluid :min="1" :max="480" />
                                        <label for="newChoreFormEstimatedMinutes">{{ $t("estimatedDurationmin")
                                            }}*</label>
                                    </FloatLabel>
                                    <Message v-if="$form.newChoreFormEstimatedMinutes?.invalid" severity="error"
                                        size="small" variant="simple">
                                        {{
                                            $form.newChoreFormEstimatedMinutes.error?.message }}</Message>
                                </div>
                            </div>
                            <div>
                                <FloatLabel variant="in" fluid>
                                    <Select id="newChoreFormCategoryId" name="newChoreFormCategoryId"
                                        :options="sortedCategories" filter optionLabel="name" option-value="id"
                                        class="w-full" showClear>
                                        <template #footer>
                                            <div class="p-3">
                                                <Button :label="$t('settings.categories.addCategory')" fluid
                                                    severity="secondary" variant="text" size="small" icon="pi pi-plus"
                                                    @click="openCreateCategoryDialog" />
                                            </div>
                                        </template>
                                    </Select>
                                    <label for="newChoreFormCategoryId">{{ $t("category") }}*</label>
                                </FloatLabel>
                                <Message v-if="$form.newChoreFormCategoryId?.invalid" severity="error" size="small"
                                    variant="simple">{{
                                        $form.newChoreFormCategoryId.error?.message }}</Message>
                            </div>
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <FloatLabel variant="in">
                            <DatePicker name="dueDate" fluid showIcon iconDisplay="input" dateFormat="dd.mm.yy"
                                :minDate="new Date()" :manualInput="false" showButtonBar />
                            <label for="dueDate" class="block text-sm font-medium text-[var(--body-text-color)] mb-1">{{
                                $t("dueAt")
                            }}*</label>
                        </FloatLabel>
                        <Message v-if="$form.dueDate?.invalid" severity="error" size="small" variant="simple">{{
                            $form.dueDate.error?.message }}</Message>
                    </div>
                    <div>
                        <FloatLabel variant="in" fluid>
                            <Select id="assignedUserId" name="assignedUserId" :options="householdUsers" filter
                                optionLabel="username" option-value="id" class="w-full" showClear />
                            <label for="assignedUserId">{{ $t("assignedTo") }}</label>
                        </FloatLabel>
                    </div>
                </div>
            </form>
        </div>
        <template #footer>
            <Button @click="closeModal" severity="secondary" :label="$t('cancel')" outlined />
            <Button @click="(formRef! as any).submit()" severity="secondary" :label="$t('save')" />
        </template>
    </Dialog>

    <AddCategoryDialog v-model:visible="showCreateCategoryDialog" @submitted="handleCategoryCreated"
        @cancel="handleCategoryDialogCancel" />
</template>

<script setup lang="ts">
import AddCategoryDialog from "@/components/settings/AddCategoryDialog.vue";
import { ref, computed, watch, onMounted, useTemplateRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useTaskBoardStore, useUsersStore, useChoreSettingsStore } from '@/stores';
import { DifficultyLevel, type CreateChoreDto } from "@/client";
import { useToast } from "primevue/usetoast";
import { Form, type FormSubmitEvent } from '@primevue/forms';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const showCreateCategoryDialog = ref(false);

const props = defineProps<{
    showModal: boolean;
    existingDueChoreIds?: string[]; // IDs of chores already in the parent's due list
}>();

const formRef = useTemplateRef('formRef');

const emit = defineEmits<{
    (e: 'update:showModal', value: boolean): void;
}>();

// Stores
const choreSettingsStore = useChoreSettingsStore();
const taskBoardStore = useTaskBoardStore();
const usersStore = useUsersStore();

const { createChoreTemplate, ensureChoreSettingsDataLoaded } = choreSettingsStore;
const { scheduleChore } = taskBoardStore

const { categories, choreTemplates } = storeToRefs(choreSettingsStore);
const { users } = storeToRefs(usersStore);
const toast = useToast();

const nextSundayDate = computed(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 (Sunday) to 6 (Saturday)
    const daysUntilSunday = (7 - dayOfWeek) % 7;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + (daysUntilSunday === 0 ? 7 : daysUntilSunday)); // if today is sunday, get next sunday
    nextSunday.setHours(0, 0, 0, 0);
    return nextSunday;
});


const initialValues = ref({
    selectedChoreId: '',
    newChoreFormName: '',
    newChoreFormDescription: null,
    newChoreFormDifficultyLevel: '',
    newChoreFormEstimatedMinutes: 15,
    newChoreFormCategoryId: '',
    dueDate: nextSundayDate.value,
    assignedUserId: null,
})
const resolver = computed(() => {
    //default resolvers (dueDate)
    const resolvers: Record<string, any> = {
        dueDate: z.date().min(new Date()),
        assignedUserId: z.string().uuid().nullish(), // optional since it can be left empty

    };
    if (tabValue.value === '0') {
        resolvers["selectedChoreId"] =
            z.string().uuid({ message: 'Bitte wähle eine Aufgabe aus.' });
    } else if (tabValue.value === '1') {
        resolvers["newChoreFormName"] = z.string()
            .min(1, { message: 'Bitte gib einen Namen ein.' })
            .min(3, { message: 'Der Name muss mindestens 3 Zeichen lang sein.' })
            .max(100, { message: 'Der Name darf maximal 100 Zeichen lang sein.' });
        resolvers["newChoreFormDescription"] = z.string().max(500, { message: 'Die Beschreibung darf maximal 500 Zeichen lang sein.' }).nullish();
        resolvers["newChoreFormDifficultyLevel"] = z.enum(Object.values(DifficultyLevel), { message: 'Bitte wähle eine Schwierigkeit aus.' });
        resolvers["newChoreFormEstimatedMinutes"] = z.number()
            .min(1, { message: 'Die geschätzte Dauer muss mindestens 1 Minute betragen.' })
            .max(480, { message: 'Die geschätzte Dauer darf maximal 480 Minuten betragen.' });
        resolvers["newChoreFormCategoryId"] = z.string()
            .uuid({ message: 'Bitte wähle eine Kategorie aus.' });
    }

    return zodResolver(
        z.object({ ...resolvers })
    );
});

const tabValue = ref<'0' | '1'>('0');

// Data for dropdowns
const sortedDifficulties = [
    { value: DifficultyLevel.EASY, label: t('difficultyLevels.' + DifficultyLevel.EASY) },
    { value: DifficultyLevel.MEDIUM, label: t('difficultyLevels.' + DifficultyLevel.MEDIUM) },
    { value: DifficultyLevel.HARD, label: t('difficultyLevels.' + DifficultyLevel.HARD) },
    { value: DifficultyLevel.VERY_HARD, label: t('difficultyLevels.' + DifficultyLevel.VERY_HARD) },
];
const sortedCategories = computed(() =>
    [...(categories.value || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
);
const householdUsers = computed(() => [...(users.value || [])].sort((a, b) => (a.username || '').localeCompare(b.username || '')));

const selectableChores = computed(() => {
    return [...(choreTemplates.value || [])]
        .filter(c => !c.isOneTimeTask)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(chore => ({
            id: chore.id,
            name: chore.name,
            disabled: isChoreAlreadyDue(chore.id!)
        }));
});

const isChoreAlreadyDue = (choreId: string) => {
    return props.existingDueChoreIds?.includes(choreId) ?? false;
};

// Lifecycle and watchers
onMounted(async () => {
    await ensureChoreSettingsDataLoaded();
    resetForm();
});

watch(() => props.showModal, (newValue) => {
    if (newValue) {
        resetForm();
    }
});

watch(tabValue, () => {
    (formRef.value as any)?.reset();
});

// Methods
function resetForm() {
    tabValue.value = '0';

    Object.assign(initialValues.value, {
        selectedChoreId: '',
        newChoreFormName: '',
        newChoreFormDescription: null,
        newChoreFormDifficultyLevel: '',
        newChoreFormEstimatedMinutes: 15,
        newChoreFormCategoryId: '',
        dueDate: null,
        assignedUserId: null,
    });
    (formRef.value as any)?.reset();
}

function closeModal() {
    emit('update:showModal', false);
}

async function handleSubmit(e: FormSubmitEvent<Record<string, any>>) {
    if (!e.valid) {
        console.error("Form validation failed:", e.errors);
        return;
    }
    if (tabValue.value === '1') {
        const newChoreForm: CreateChoreDto = {
            name: e.values.newChoreFormName,
            description: e.values.newChoreFormDescription,
            difficultyLevel: e.values.newChoreFormDifficultyLevel,
            estimatedMinutes: e.values.newChoreFormEstimatedMinutes,
            categoryId: e.values.newChoreFormCategoryId,
            autoSchedule: false,
            isOneTimeTask: true,
        };

        const newChore = await createChoreTemplate(newChoreForm);
        e.values.selectedChoreId = newChore.id!;
    }

    await scheduleChore({
        assignedUserId: e.values.assignedUserId,
        dueAt: new Date(e.values.dueDate),
        choreId: e.values.selectedChoreId,
    })
    toast.add({
        severity: 'success',
        summary: 'Erfolg',
        detail: 'Aufgabe wurde eingereiht',
        life: 3000
    });
    console.log("Form submitted:", e);
    closeModal();
}

function openCreateCategoryDialog() {
    showCreateCategoryDialog.value = true;
}

function handleCategoryCreated() {
    showCreateCategoryDialog.value = false;
    toast.add({
        severity: 'success',
        summary: t('settings.categories.categoryCreated'),
        detail: t('settings.categories.categoryCreatedDetail'),
        life: 3000
    });
}

function handleCategoryDialogCancel() {
    showCreateCategoryDialog.value = false;
}
</script>

<style scoped>
/* Add any specific styles if needed */
</style>
